import moment from 'moment';
import { routes } from '@app/consts';

export interface TimeOffManagerPayload {
  requestId: string;
  employeeName: string;
  startDate: string | null;
  endDate: string | null;
  message: string;
}

export interface TimeOffEmployeePayload {
  requestId: string;
  startDate: string | null;
  endDate: string | null;
  statusComments: string | null;
  message: string;
}

export interface BuySellRequestPayload {
  buySellId: string;
  type: 'BUY' | 'SELL' | null;
  hours: number | null;
  /** Only sent to reviewers (reviewpending). */
  employeeName?: string;
  message: string;
}

/** Every event the API currently emits on /hubs/notifications, with its payload. */
export type NotificationEventMap = {
  'reporting.reportrun.started': { reportRunId: string; reportId: string };
  'reporting.reportrun.completed': { reportRunId: string; reportId: string };
  'reporting.reportrun.failed': { reportRunId: string; reportId: string; error: string };
  'import.importstarted': { message: string };
  'import.importfailed': { message: string };
  'import.importfinished': { message: string };
  'performance.goalplan.publishstarted': { message: string };
  'performance.goalplan.publishfinished': { message: string };
  'performance.reviewplan.publishstarted': { message: string };
  'performance.reviewplan.publishfinished': { message: string };
  'buysell.publishstarted': { message: string };
  'buysell.publishfinished': { message: string };
  'timeoff.request.created': TimeOffManagerPayload;
  'timeoff.request.edited': TimeOffManagerPayload;
  'timeoff.request.approved': TimeOffEmployeePayload;
  'timeoff.request.denied': TimeOffEmployeePayload;
  'buysell.request.reviewpending': BuySellRequestPayload;
  'buysell.request.approved': BuySellRequestPayload;
  'buysell.request.denied': BuySellRequestPayload;
};

export type NotificationEventName = keyof NotificationEventMap;
/** 'info' is a job in progress; 'action' is something waiting on the user. */
export type NotificationStatus = 'info' | 'action' | 'success' | 'error';
export type NotificationCategory =
  'report' | 'import' | 'goalPlan' | 'reviewPlan' | 'buySell' |
  'timeOffApproval' | 'timeOffRequest' | 'buySellReview' | 'buySellRequest';

export interface NotificationEventConfig {
  category: NotificationCategory;
  status: NotificationStatus;
  titleKey: string;
  titleDefault: string;
  /** Show a snackbar when the event arrives (finished / failed jobs only). */
  toast: boolean;
  /** Snackbar type when it differs from status (error snackbars read as "Something went wrong"). */
  toastType?: string;
}

export interface CategoryConfig {
  icon: string;
  link: (payload: any) => string;
  /** Identifies one job, so a finished event replaces its started event in the list. */
  jobKey: (payload: any) => string;
  /** Builds translated detail text; the payload's own message is English only. */
  message?: (payload: any, translate: Translate) => string;
}

export type Translate = (key: string, fallback: string) => string;

/** Payload dates have no time zone, so they are read as calendar dates. */
function dateRange(start: string | null, end: string | null): string {
  const toDate = (d: string | null) => (d ? moment(d.substring(0, 10), 'YYYY-MM-DD') : null);
  const from = toDate(start);
  const to = toDate(end);
  if (from && to && !from.isSame(to, 'day')) return `${from.format('ll')} – ${to.format('ll')}`;
  return (from ?? to)?.format('ll') ?? '';
}

function joinParts(...parts: (string | null | undefined)[]): string {
  return parts.filter((p) => p).join(' · ');
}

function buySellDetail(p: BuySellRequestPayload, t: Translate): string {
  const type = p.type === 'BUY' ? t('Notification-Buy', 'Buy') : p.type === 'SELL' ? t('Notification-Sell', 'Sell') : null;
  const hours = p.hours != null ? `${p.hours} ${t('Notification-Hours', 'hours')}` : null;
  return joinParts(p.employeeName, type, hours);
}

export const CATEGORY_CONFIG: Record<NotificationCategory, CategoryConfig> = {
  report: {
    icon: 'assessment',
    link: (p) => `${routes.REPORTS}/Runs/${p.reportRunId}`,
    jobKey: (p) => `report:${p.reportRunId}`
  },
  import: {
    icon: 'file_upload',
    link: () => `/${routes.IMPORTS}`,
    jobKey: () => 'import'
  },
  goalPlan: {
    icon: 'adjust',
    link: () => `${routes.PERFORMANCE}${routes.GOAL_PLANS}`,
    jobKey: () => 'goalPlan'
  },
  reviewPlan: {
    icon: 'rate_review',
    link: () => `${routes.PERFORMANCE}${routes.REVIEW_PLANS}`,
    jobKey: () => 'reviewPlan'
  },
  buySell: {
    icon: 'swap_horiz',
    link: () => `${routes.TIME_OFF}${routes.BUY_SELL}${routes.BUY_SELL_PLANS}`,
    jobKey: () => 'buySell'
  },
  timeOffApproval: {
    icon: 'event_available',
    link: () => routes.DASHBOARD,
    jobKey: (p) => `timeOff:${p.requestId}`,
    message: (p: TimeOffManagerPayload) => joinParts(p.employeeName, dateRange(p.startDate, p.endDate))
  },
  timeOffRequest: {
    icon: 'event_available',
    link: () => routes.HOMEPAGE,
    jobKey: (p) => `timeOff:${p.requestId}`,
    message: (p: TimeOffEmployeePayload) => joinParts(dateRange(p.startDate, p.endDate), p.statusComments)
  },
  buySellReview: {
    icon: 'swap_horiz',
    link: () => `${routes.TIME_OFF}${routes.BUY_SELL}${routes.APPROVALS}`,
    jobKey: (p) => `buySellRequest:${p.buySellId}`,
    message: buySellDetail
  },
  buySellRequest: {
    icon: 'swap_horiz',
    link: () => routes.HOMEPAGE,
    jobKey: (p) => `buySellRequest:${p.buySellId}`,
    message: buySellDetail
  }
};

export const EVENT_CONFIG: Record<NotificationEventName, NotificationEventConfig> = {
  'reporting.reportrun.started': { category: 'report', status: 'info', toast: false, titleKey: 'Notification-ReportRunStarted', titleDefault: 'Your report is being generated' },
  'reporting.reportrun.completed': { category: 'report', status: 'success', toast: true, titleKey: 'Notification-ReportRunCompleted', titleDefault: 'Your report is ready' },
  'reporting.reportrun.failed': { category: 'report', status: 'error', toast: true, titleKey: 'Notification-ReportRunFailed', titleDefault: 'Your report failed' },
  'import.importstarted': { category: 'import', status: 'info', toast: false, titleKey: 'Notification-ImportStarted', titleDefault: 'Import started' },
  'import.importfailed': { category: 'import', status: 'error', toast: true, titleKey: 'Notification-ImportFailed', titleDefault: 'Import failed' },
  'import.importfinished': { category: 'import', status: 'success', toast: true, titleKey: 'Notification-ImportFinished', titleDefault: 'Import finished' },
  'performance.goalplan.publishstarted': { category: 'goalPlan', status: 'info', toast: false, titleKey: 'Notification-GoalPlanPublishStarted', titleDefault: 'Publishing goal plan' },
  'performance.goalplan.publishfinished': { category: 'goalPlan', status: 'success', toast: true, titleKey: 'Notification-GoalPlanPublishFinished', titleDefault: 'Goal plan published' },
  'performance.reviewplan.publishstarted': { category: 'reviewPlan', status: 'info', toast: false, titleKey: 'Notification-ReviewPlanPublishStarted', titleDefault: 'Publishing review plan' },
  'performance.reviewplan.publishfinished': { category: 'reviewPlan', status: 'success', toast: true, titleKey: 'Notification-ReviewPlanPublishFinished', titleDefault: 'Review plan published' },
  'buysell.publishstarted': { category: 'buySell', status: 'info', toast: false, titleKey: 'Notification-BuySellPublishStarted', titleDefault: 'Publishing buy/sell plan' },
  'buysell.publishfinished': { category: 'buySell', status: 'success', toast: true, titleKey: 'Notification-BuySellPublishFinished', titleDefault: 'Buy/sell plan published' },
  'timeoff.request.created': { category: 'timeOffApproval', status: 'action', toast: true, titleKey: 'Notification-TimeOffRequestCreated', titleDefault: 'New time off request' },
  'timeoff.request.edited': { category: 'timeOffApproval', status: 'action', toast: true, titleKey: 'Notification-TimeOffRequestEdited', titleDefault: 'Time off request edited' },
  'timeoff.request.approved': { category: 'timeOffRequest', status: 'success', toast: true, titleKey: 'Notification-TimeOffRequestApproved', titleDefault: 'Your time off request was approved' },
  'timeoff.request.denied': { category: 'timeOffRequest', status: 'error', toast: true, toastType: 'info', titleKey: 'Notification-TimeOffRequestDenied', titleDefault: 'Your time off request was denied' },
  'buysell.request.reviewpending': { category: 'buySellReview', status: 'action', toast: true, titleKey: 'Notification-BuySellRequestReviewPending', titleDefault: 'Buy/sell request awaiting your review' },
  'buysell.request.approved': { category: 'buySellRequest', status: 'success', toast: true, titleKey: 'Notification-BuySellRequestApproved', titleDefault: 'Your buy/sell request was approved' },
  'buysell.request.denied': { category: 'buySellRequest', status: 'error', toast: true, toastType: 'info', titleKey: 'Notification-BuySellRequestDenied', titleDefault: 'Your buy/sell request was denied' }
};

export const EVENT_NAMES = Object.keys(EVENT_CONFIG) as NotificationEventName[];
